import { CheckCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Divider,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { createSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import Config from '../app/config';
import ErrorResult from '../components/ErrorResult';
import Form from '../components/Form';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { InterviewDetail, JobPostingId } from '../types';
import styles from './CandidateInterviewOpinion.module.css';

interface CandidateInterviewOpinionFormValues {
  opinionStatus: 'QUALIFIED' | 'NOT_QUALIFIED' | 'IN_ANALYSIS';
  opinionJustification: string;
}

const CandidateInterviewOpinion = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { tenant, interview_id, id } = useTypedParams({
    tenant: String,
    id: JobPostingId,
    interview_id: Number,
  });

  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();

  const interview = useAPIQuery(`/interviews/${interview_id}/`);

  const recruitmentProcess = useAPIQuery(
    `/recruitment-processes/${interview.data?.recruitment_process}/`,
    { enabled: Boolean(interview.data?.recruitment_process) }
  );

  const stage = useAPIQuery(`/stages/${interview.data?.stage}/`, {
    enabled: Boolean(interview.data?.recruitment_process),
  });

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const [isSavedVisible, setIsSavedVisible] = useState(false);

  const updateOpinionMutation = useAPIMutation(
    async (
      {
        opinionJustification,
      }: { opinionJustification: CandidateInterviewOpinionFormValues['opinionJustification'] },
      api
    ) => {
      await api.patch(`/interviews/${interview_id}/`, {
        opinion_justification: opinionJustification,
      });
    },
    {
      onSuccess: () => {
        setIsSavedVisible(true);
      },
    }
  );

  const debounceUpdateJustification = useDebouncedCallback(
    async ({ opinionJustification }: CandidateInterviewOpinionFormValues) => {
      updateOpinionMutation.mutate({
        opinionJustification,
      });
    },
    Config.REACT_APP_AUTOSAVE_DELAY
  );

  const registerOpinion = useAPIMutation(
    async (
      {
        opinionJustification,
        opinionStatus,
      }: {
        opinionJustification: InterviewDetail['opinion_justification'];
        opinionStatus: InterviewDetail['opinion_status'];
      },
      api
    ) => {
      return await api.patch(`/interviews/${interview_id}/`, {
        opinion_justification: opinionJustification,
        opinion_status: opinionStatus,
      });
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/interviews/');
        await queryClient.invalidateQueries(`/interviews/${interview_id}/`);
      },
      onSuccess: async ({ data }) => {
        message.success(t('Parecer registrado com sucesso'));
        navigate({
          pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/interviews/',
          params: {
            tenant,
            id,
            applicant_id: recruitmentProcess.data?.candidate.id,
          },
          search: createSearchParams(
            `stages=${data.stage}&status=ACTIVE&sort=match&direction=descend`
          ),
        });
      },
    }
  );

  return (
    <Page>
      <ErrorResult
        error={recruitmentProcess.error || stage.error || interview.error || jobPosting.error}
      >
        <Spin
          spinning={
            recruitmentProcess.isLoading ||
            stage.isLoading ||
            interview.isLoading ||
            jobPosting.isLoading
          }
        >
          <Helmet>
            <title>
              {t('Entrevista')} | {recruitmentProcess.data?.candidate?.name || '...'} |{' '}
              {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <PageTitle
            title={`${t('Entrevista')} | ${recruitmentProcess.data?.candidate.name || '...'}`}
            level={2}
          />
          <PageContent>
            <Card>
              <Form<CandidateInterviewOpinionFormValues>
                form={form}
                layout="vertical"
                initialValues={{
                  id: interview.data?.id,
                  opinionJustification: interview.data?.opinion_justification || '',
                  opinionStatus:
                    interview.data?.opinion_status !== 'PENDING'
                      ? interview.data?.opinion_status
                      : null,
                }}
                onValuesChange={(changedValues, values) => {
                  const changedKeys = Object.keys(changedValues);

                  if (changedKeys.includes('opinionJustification')) {
                    debounceUpdateJustification({
                      opinionJustification: values.opinionJustification,
                      opinionStatus: values.opinionStatus,
                    });

                    if (isSavedVisible) {
                      setIsSavedVisible(false);
                    }
                  }
                }}
                onFinish={async ({ opinionJustification, opinionStatus }) => {
                  debounceUpdateJustification.cancel();

                  const interview = await queryClient.fetchQuery<InterviewDetail>(
                    `/interviews/${interview_id}/`
                  );

                  if (['QUALIFIED', 'NOT_QUALIFIED'].includes(interview.opinion_status || '')) {
                    return Modal.warn({
                      title: t('Parecer já registrado'),
                      content: t(
                        'Já existe um parecer enviado para este candidato. Caso queira complementar, acesse a vaga e edite o parecer existente.'
                      ),
                      okText: t('Voltar a tela de candidatos'),
                      maskClosable: true,
                      onOk: () =>
                        navigate({
                          pathname: '/:tenant/opportunities/:id/analysis/',
                          params: {
                            tenant,
                            id,
                          },
                          search: createSearchParams(
                            `stages=${
                              recruitmentProcess.data?.stage || ''
                            }&status=ACTIVE&sort=match&direction=descend`
                          ),
                        }),
                    });
                  }

                  registerOpinion.mutate({ opinionJustification, opinionStatus });
                }}
              >
                <Form.Row>
                  <Form.Col span={12}>
                    <Form.Item
                      label={t('Parecer da etapa')}
                      name="opinionStatus"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={[
                          { key: 'IN_ANALYSIS', label: t('Em análise'), value: 'IN_ANALYSIS' },
                          { key: 'QUALIFIED', label: t('Qualificado'), value: 'QUALIFIED' },
                          {
                            key: 'NOT_QUALIFIED',
                            label: t('Não qualificado'),
                            value: 'NOT_QUALIFIED',
                          },
                        ]}
                      />
                    </Form.Item>
                  </Form.Col>
                  <Form.Col flex={1} />
                  <Button
                    key="finish"
                    type="primary"
                    loading={registerOpinion.isLoading}
                    onClick={() => form.submit()}
                  >
                    <Trans>Registrar parecer</Trans>
                  </Button>
                </Form.Row>
                <Divider />
                <Form.Row>
                  <Form.Col span={12}>
                    <Space direction="vertical" size="middle">
                      <Form.Item
                        label={t('Justificativa do parecer')}
                        name="opinionJustification"
                        rules={[{ required: true }]}
                        required
                      >
                        <Input.TextArea rows={15} />
                      </Form.Item>
                      <Row justify="space-between" align="middle">
                        {isSavedVisible ? (
                          <Typography.Text>
                            <CheckCircleOutlined className={styles.success} />
                            <Trans>Salvo</Trans>
                          </Typography.Text>
                        ) : (
                          <div style={{ height: 22 }} />
                        )}
                        <Button
                          loading={updateOpinionMutation.isLoading}
                          onClick={async () => {
                            debounceUpdateJustification.cancel();
                            if (isSavedVisible) {
                              setIsSavedVisible(false);
                            }
                            try {
                              await form.validateFields();
                              await updateOpinionMutation.mutateAsync({
                                opinionJustification: form.getFieldValue('opinionJustification'),
                              });
                              message.success(t('Parecer atualizado com sucesso!'));
                            } catch (e) {
                              return undefined;
                            }
                          }}
                        >
                          <Trans>Salvar</Trans>
                        </Button>
                      </Row>
                    </Space>
                  </Form.Col>
                  <Form.Col span={12}>
                    <Space direction="vertical" size="middle">
                      <Form.Item
                        label={
                          <Typography.Text strong>
                            <Trans>Anotações da etapa</Trans>
                          </Typography.Text>
                        }
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: interview.data?.annotations || '',
                          }}
                        />
                      </Form.Item>
                    </Space>
                  </Form.Col>
                </Form.Row>
              </Form>
            </Card>
          </PageContent>
        </Spin>
      </ErrorResult>
    </Page>
  );
};

export default CandidateInterviewOpinion;
